// The minimum-margin check on store prices (client sheet C09, owner decision G-G1).
//
// Margin = (price - cost) / price, both before tax: the same margin
// `marginPercent` (utils/currency.ts) shows everywhere in the admin. Every
// amount is in minor units, and the maths stays in whole numbers — never via
// major units (a 3-decimal currency would round wrong).

/** a / b rounded up, for whole numbers a >= 0 and b > 0, with no float division. */
function ceilDiv(a: number, b: number): number {
  const rest = a % b;
  const whole = (a - rest) / b;
  return rest === 0 ? whole : whole + 1;
}

/**
 * The lowest price (minor units) that keeps `minPct` margin over `cost`, or
 * null when there is nothing to check: no minimum, or a cost of 0 / unknown.
 * `minPct` is 0 to 99.99 with up to 2 decimals, so it is scaled to hundredths.
 */
export function lowestPriceForMargin(
  cost: number | null | undefined,
  minPct: number | null | undefined,
): number | null {
  if (minPct === null || minPct === undefined || !cost || cost <= 0) return null;
  // not-money: a percent (25.5) to hundredths of a percent (2550), not a currency factor.
  const hundredths = Math.round(minPct * 100);
  if (hundredths < 0 || hundredths >= 10000) return null;
  return ceilDiv(Math.round(cost) * 10000, 10000 - hundredths);
}

/** True when `price` leaves less than `minPct` margin over `cost`. Never true without a minimum or a cost. */
export function isBelowMinMargin(
  price: number | null | undefined,
  cost: number | null | undefined,
  minPct: number | null | undefined,
): boolean {
  const lowest = lowestPriceForMargin(cost, minPct);
  return lowest !== null && price !== null && price !== undefined && price < lowest;
}

/** "25%" or "25.5%" — a minimum as the owner typed it. */
export function formatPct(pct: number): string {
  return `${Number(pct.toFixed(2))}%`;
}
